import { useEffect, useState, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import fleetWorkOrderAtom from '../../atoms/fleetWorkOrderAtom';
import axios from 'axios';
import { url } from '../../utils/config';
import { label, btnTwo, btnOne } from '../../utils/style';
import { TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import returnDiscountedAmount from '../../helper_functions/returnDiscountedAmount';
import FleetWorkOrderModal from './FleetWorkOrderModal';
import estimateFleetImageAtom from '../../atoms/estimateFleetImageAtom';
import formatNumberToUs from '../../helper_functions/formatNumberToUs';
import loaderStateAtom from '../../atoms/loaderStateAtom';
import ConfirmationModal from '../ConfirmationModal';
import globalConfigAtom from '../../atoms/globalConfigAtom';


function FleetCreateWorkOrder({ setOrderState, appointmentDetails, workOrderId }) {
  const [modalState, setModalState] = useState(null);
  const [workOrder, setWorkOrder] = useRecoilState(fleetWorkOrderAtom);
  const [estimatedDate, setEstimatedDate] = useState(workOrder?.estimatedDate || '');
  const [estimatedExpirationDate, setEstimatedExpirationDate] = useState('');
  const [estimatedTime, setEstimatedTime] = useState(workOrder?.estimatedTime || '');
  const [allServices, setAllServices] = useState({})
  const globalConfig = useRecoilValue(globalConfigAtom);
  const [images, setImages] = useRecoilState(estimateFleetImageAtom);
  const [showLoader, setShowLoader] = useRecoilState(loaderStateAtom)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteImage, setDeleteImage] = useState(null);
  const inputRef = useRef(null);
  const [maxImageSize, setMaxImageSize] = useState(2);
  const MAX_IMAGES = 10 - (workOrder.images?.length || 0);

  async function fetchMaxEstimateImageSize() {
    try {
      const token = window.localStorage.getItem('token');
      const headers = { authorization: `bearer ${token}` };
      const res = await axios.get(url + '/default_estimate_images_size', { headers });
      if (res.data?.maxEstimateImageSize) {
        setMaxImageSize(Number(res.data.maxEstimateImageSize))
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (workOrder?.estimatedExpirationDate) {
      setEstimatedExpirationDate(workOrder.estimatedExpirationDate);
    } else {
      const daysToAdd = Number(globalConfig?.estimateExpirationDay || 0);
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() + daysToAdd);
      const formatted = currentDate.toISOString().split('T')[0];
      setWorkOrder(prev => ({
        ...prev,
        estimatedExpirationDate: formatted
      }));
      setEstimatedExpirationDate(formatted);
    }
  }, [workOrder, globalConfig]);


  // Update workOrder when date or time changes
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setEstimatedDate(newDate);
    setWorkOrder(prev => ({
      ...prev,
      estimatedDate: newDate
    }));
  };

  const handleExpirationDateChange = (e) => {
    const newDate = e.target.value;
    setEstimatedExpirationDate(newDate);
    setWorkOrder(prev => ({
      ...prev,
      estimatedExpirationDate: newDate
    }));
  };

  const handleTimeChange = (e) => {
    const time = e.target.value;
    const [hour, minutes] = time.split(':')
    const hourInt = parseInt(hour);
    const ampm = hourInt >= 12 ? 'PM' : 'AM';
    const hour12 = hourInt % 12 || 12;
    const newTime = `${hour12}:${minutes} ${ampm}`;

    setEstimatedTime(time);



    setWorkOrder(prev => ({
      ...prev,
      estimatedTime: time
    }));
  };

  useEffect(() => { fetchAllServices(); fetchMaxEstimateImageSize(); }, []);


  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const oversizedFile = selectedFiles.find(file => file.size > maxImageSize * 1024 * 1024);
    if (oversizedFile) {
      toast.error(`File "${oversizedFile.name}" exceeds ${maxImageSize}MB limit`, { autoClose: 3000, });
      e.target.value = "";
      return;
    }

    const totalImages = [...images, ...selectedFiles];
    if (totalImages.length > MAX_IMAGES) {
      toast.error(`You can only upload up to 10 images`, { autoClose: 3000, });
      e.target.value = "";
      return;
    }

    setImages(totalImages);
    e.target.value = ""; // Reset input
  };


  const removeImage = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  const removeServerImage = async (imageName) => {
    try {
      setShowLoader(true)
      const token = window.localStorage.getItem('token');
      const headers = { authorization: `bearer ${token}` };
      const resp = await axios.delete(`${url}/remove_fleet_estimate_images/${workOrderId}/${imageName}`, { headers });
      if (resp.status === 200) {
        setWorkOrder(prev => ({
          ...prev,
          images: (prev.images || []).filter(img => img !== imageName),
        }));
        setShowLoader(false)
      }

    } catch (e) {
      console.log(e)
      setShowLoader(false)
    }
  }

  async function fetchAllServices() {
    try {
      const token = window.localStorage.getItem('token');
      const headers = { authorization: `bearer ${token}` };
      const res = await axios.get(url + '/all_services', { headers });
      setAllServices(res.data)
    } catch (error) {
      console.log(error)
    }
  }

  function deleteService(index, serviceType) {
    try {
      var order = JSON.parse(JSON.stringify(workOrder))
      const list = [...workOrder[serviceType].data]
      list.splice(index, 1)
      order[serviceType].data = list
      setWorkOrder(order)
    } catch (error) {
      console.log(error)
    }
  }

  function updateWorkOrderData(data) {
    switch (modalState) {
      case 'serviceBundle':
        var order = {
          serviceBundle: { data: [...workOrder.serviceBundle.data], note: '' },
          materialList: workOrder.materialList,
          otherList: workOrder.otherList,
          serviceList: workOrder.serviceList,
          technicianCost: workOrder.technicianCost,
          id: workOrder.id,
          estimatedDate: workOrder.estimatedDate,
          estimatedTime: workOrder.estimatedTime,
          images: workOrder.images
        }
        order.serviceBundle.data.push(data);
        setWorkOrder(order)
        break;

      case 'service':
        var order = {
          serviceList: { data: [...workOrder.serviceList.data], note: '' },
          serviceBundle: workOrder.serviceBundle,
          materialList: workOrder.materialList,
          otherList: workOrder.otherList,
          technicianCost: workOrder.technicianCost,
          id: workOrder.id,
          estimatedDate: workOrder.estimatedDate,
          estimatedTime: workOrder.estimatedTime,
          images: workOrder.images
        }
        order.serviceList.data.push(data);
        setWorkOrder(order)
        break;

      case 'material':
        var order = {
          materialList: { data: [...workOrder.materialList.data], note: '' },
          serviceBundle: workOrder.serviceBundle,
          serviceList: workOrder.serviceList,
          otherList: workOrder.otherList,
          technicianCost: workOrder.technicianCost,
          id: workOrder.id,
          estimatedDate: workOrder.estimatedDate,
          estimatedTime: workOrder.estimatedTime,
          images: workOrder.images
        }
        order.materialList.data.push(data);
        setWorkOrder(order)
        break;

      case 'other':
        var order = {
          otherList: { data: [...workOrder.otherList.data], note: '' },
          serviceBundle: workOrder.serviceBundle,
          materialList: workOrder.materialList,
          serviceList: workOrder.serviceList,
          technicianCost: workOrder.technicianCost,
          id: workOrder.id,
          estimatedDate: workOrder.estimatedDate,
          estimatedTime: workOrder.estimatedTime,
          images: workOrder.images
        }
        order.otherList.data.push(data);
        setWorkOrder(order)
        break;

      default:
        break;
    }
  }

  // Convert MM-DD-YYYY to YYYY-MM-DD format
  const convertDateFormat = (dateString) => {
    if (!dateString) return '';
    const [month, day, year] = dateString.split('-');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  // Get minimum date from appointment date
  const getMinDate = () => {
    if (!appointmentDetails?.date) return '';
    const appointmentDate = convertDateFormat(appointmentDetails.date);
    const today = new Date().toISOString().split('T')[0];

    // Compare dates and return the later one
    return appointmentDate > today ? appointmentDate : today;
  };

  const handlePreview = () => {

    if (!workOrder.estimatedDate || !workOrder.estimatedTime) {
      toast.error('Please select both estimated date and time', { autoClose: 3000 });
      return;
    }

    const minDate = getMinDate();
    const estimatedCurrentDate = estimatedDate
    if (estimatedCurrentDate < minDate) {
      return toast.error('Please select a valid estimated date', { autoClose: 3000 });
    }

    if (workOrder.serviceBundle.data.length === 0 && workOrder.serviceList.data.length === 0 && workOrder.materialList.data.length === 0 && workOrder.otherList.data.length === 0) {
      toast.error('Please add at least one service bundle, service, material, or other cost', { autoClose: 3000 });
      return;
    }
    setOrderState("preview");
  }


  return (
    <div className='w-full'>
      {
        modalState &&
        <FleetWorkOrderModal
          setShowModal={setModalState}
          updateWorkOrderData={updateWorkOrderData}
          modalState={modalState}
          allServices={allServices}
        />
      }
      <div className="font-semibold m-2 text-center">Description & Pricing</div>
      <div>
        <div className="p-4 bg-white rounded-lg shadow-md mb-4 border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <div className={label}>Service Bundle</div>
            <div
              onClick={() => setModalState('serviceBundle')}
              className='text-customOne text-sm font-semibold'
            >
              {`Manage >`}
            </div>
          </div>

          {workOrder?.serviceBundle?.data?.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Mobile Cards */}
              <div className="space-y-3">
                {workOrder.serviceBundle.data.map((bundle, ind) => (
                  <div key={ind} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-gray-700 truncate capitalize">{bundle.name}</div>
                      {!bundle.existingData && <button
                        onClick={() => deleteService(ind, 'serviceBundle')}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      }
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">Cost:</span>
                        <span className="text-gray-700 font-bold">${formatNumberToUs(bundle.cost)}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">Discount:</span>
                        <span className="text-gray-700 font-bold">
                          {bundle.discountType === 'flat' && '$'}{`${formatNumberToUs(bundle.discount)} (${bundle.discountType === 'flat' ? 'Flat' : '%'})`}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">Discounted Cost:</span>
                        <span className="text-gray-700 font-bold">
                          ${formatNumberToUs(returnDiscountedAmount({ cost: bundle?.cost, discount: bundle?.discount, discountType: bundle?.discountType }))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : <></>}
        </div>
        <div className='p-4 shadow-lg rounded-lg mb-4'>
          <div className='flex justify-between items-center mb-4'>
            <div className={label}>Services Cost</div>
            <div className='text-customOne text-sm font-semibold' onClick={() => setModalState('service')}>{`Manage >`}</div>
          </div>
          {workOrder?.serviceList?.data?.length > 0 ? (
            <div className="overflow-x-auto">

              {/* Mobile Cards */}
              <div className="space-y-3">
                {workOrder.serviceList.data.map((service, ind) => (
                  <div key={ind} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-gray-700 truncate capitalize">{service.name}</div>
                      {!service.existingData && <button
                        onClick={() => deleteService(ind, 'serviceList')}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      }
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">{service.costType === 'Fixed' ? 'Cost:' : 'Time:'}</span>
                        <span className="text-gray-700 font-bold">
                          {service.costType === 'Fixed' && '$'}{service.estimatedTime || service.estimatedCost} {service.costType === 'Hourly' && 'mins'}
                        </span>
                      </div>
                      {
                        service.costType !== 'Fixed' &&
                        <div className="flex items-center">
                          <span className="text-gray-500 mr-2">Cost:</span>
                          <span className="text-gray-700 font-bold">
                            ${formatNumberToUs(((service.estimatedTime / 60) * appointmentDetails.technicianHourlyCost).toFixed(2))}
                          </span>
                        </div>
                      }
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">Discount:</span>
                        <span className="text-gray-700 font-bold">
                          {service.discountType === 'flat' && '$'}{`${formatNumberToUs(service.discount)} (${service.discountType === 'flat' ? 'Flat' : '%'})`}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">Discounted Cost:</span>
                        <span className="text-gray-700 font-bold">
                          ${formatNumberToUs(returnDiscountedAmount({ cost: service.estimatedTime ? ((service.estimatedTime / 60) * appointmentDetails.technicianHourlyCost).toFixed(2) : service.estimatedCost, discount: service?.discount, discountType: service?.discountType }))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : <></>}
        </div>
        <div className=' p-4 shadow-lg rounded-lg mb-4'>
          <div className='flex justify-between items-center'>
            <div className={label}>Other Cost</div>
            <div className='text-customOne text-sm font-semibold' onClick={() => setModalState('other')}>{`Manage >`}</div>
          </div>
          <div className="space-y-3 mt-3">
            {workOrder.otherList.data.map((other, ind) => (
              <div key={ind} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-bold text-gray-700 truncate capitalize">{other.name}</div>
                  {!other.existingData && <button
                    onClick={() => deleteService(ind, 'otherList')}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>}
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Time:</span>
                    <span className="text-gray-700 font-bold">{other.estimatedTime} mins</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Cost:</span>
                    <span className="text-gray-700 font-bold">
                      ${formatNumberToUs(((other.estimatedTime / 60) * appointmentDetails.technicianHourlyCost).toFixed(2))}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Discount:</span>
                    <span className="text-gray-700 font-bold">
                      {other.discountType === 'flat' && '$'}{`${formatNumberToUs(other.discount)} (${other.discountType === 'flat' ? 'Flat' : '%'})`}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Discounted Cost:</span>
                    <span className="text-gray-700 font-bold">
                      ${formatNumberToUs(returnDiscountedAmount({ cost: ((other.estimatedTime / 60) * appointmentDetails.technicianHourlyCost).toFixed(2), discount: other?.discount, discountType: other?.discountType }))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className=' p-4 shadow-lg rounded-lg mb-4'>
          <div className='flex justify-between items-center'>
            <div className={label}>Parts Cost</div>
            <div className='text-customOne text-sm font-semibold' onClick={() => setModalState('material')}>{`Manage >`}</div>
          </div>
          <div className='mt-3'>
            {workOrder.materialList.data.map((material, ind) => (
              <div key={ind} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-bold capitalize text-gray-700 truncate">{material.name}</div>
                  {!material.existingData && <button
                    onClick={() => deleteService(ind, 'materialList')}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                  }
                </div>


                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Time:</span>
                    <span className="text-gray-700 font-semibold">{material.estimatedTime} mins</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Cost:</span>
                    <span className="text-gray-700 font-semibold">${formatNumberToUs(material.cost)}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Sales Tax:</span>
                    <span className="text-gray-700 font-semibold">{appointmentDetails.taxRatePercent}%</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Discount:</span>
                    <span className="text-gray-700 font-bold">
                      {material.discountType === 'flat' && '$'}{`${formatNumberToUs(material.discount)} (${material.discountType === 'flat' ? 'Flat' : '%'})`}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Discounted Cost:</span>
                    <span className="text-gray-700 font-bold">
                      ${formatNumberToUs(returnDiscountedAmount({ cost: material?.cost, discount: material?.discount, discountType: material?.discountType }))}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>


      <div className="w-full p-4 bg-white rounded-lg shadow-md mb-4 border border-gray-100">
        <button
          disabled={images.length >= MAX_IMAGES}
          onClick={() => inputRef.current.click()}
          className={`${btnTwo} ${images.length >= MAX_IMAGES ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          Upload Images
        </button>

        <input
          type="file"
          multiple
          accept=".png, .jpg, .jpeg"
          onChange={handleImageChange}
          ref={inputRef}
          className="hidden"
        />

        {showConfirmModal && <ConfirmationModal
          header={'Delete'}
          body={'Are you sure you want to delete this image?'}
          cancelHandler={() => {
            setShowConfirmModal(false);
            setDeleteImage(null);   // reset when cancel/close
          }}
          confirmHandler={() => {
            removeServerImage(deleteImage);
            setDeleteImage(null);   // reset after delete
          }}
        />}

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          {/* Existing uploaded images */}
          {workOrder.images?.map((imageName, index) => (
            <div key={`server-${index}`} className="relative group">
              <img
                src={`${url}/fleet_estimate_images/${imageName}`}
                alt={`uploaded-${index}`}
                className="w-full h-32 object-cover rounded-sm"
              />
              <button
                onClick={() => { setDeleteImage(imageName); setShowConfirmModal(true) }}
                className="absolute top-1 right-1 bg-gray-600 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition"
              >
                ✕
              </button>
            </div>
          ))}

          {/* Newly uploaded images (not yet saved to server) */}
          {images.map((image, index) => (
            <div key={`local-${index}`} className="relative group">
              <img
                src={URL.createObjectURL(image)}
                alt={`preview-${index}`}
                className="w-full h-32 object-cover rounded-sm"
              />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 bg-gray-600 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>


      {/* Estimate Completion Date and Time */}
      <div className='w-full p-3'>
        <div className='text-md font-semibold'>Estimated Completion Date and Time</div>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <input
              type="date"
              value={estimatedDate}
              required
              min={getMinDate()}
              onChange={handleDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-customOne"
            />
          </div>
          <div>
            <input
              type="time"
              value={estimatedTime}
              required
              onChange={handleTimeChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-customOne"
            />
          </div>
        </div>
        {appointmentDetails.bypassApproval === "no" && (
          <>
            <div className='text-md font-semibold mt-2'>Estimate Expiration Date</div>
            <input
              type="date"
              value={estimatedExpirationDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={handleExpirationDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-customOne"
            />
          </>
        )}
      </div>
      <div className="flex justify-between items-center w-full p-3">
        <button onClick={() => handlePreview()} className={btnOne}>Next</button>
      </div>
    </div>
  )
}

export default FleetCreateWorkOrder;